import { useCallback, useEffect, useRef, useState } from 'react';
import { SITE_ANNOUNCEMENT_LIST_PATH, SiteAnnouncement, SiteAnnouncementDurationType, SiteAnnouncementFormData, SiteAnnouncementStatus, SiteAnnouncementType } from 'core/siteAnnouncement/SiteAnnouncement';
import { useCallAPI } from 'hooks/useCallAPI';
import { SelectOptions } from 'components/commonType';
import moment from 'moment';
import { AgencyManager, ChooseVendorStrategy, DefaultAgencyManager } from 'core';
import i18n from 'i18n';
import { URL_REGEX, validateEmpty, validateMinimum, validateStringMaxLength, validateValidWords } from 'utils/ValidateUtils';
import { flatMap, isEmpty, omitBy } from 'lodash';
import { DefaultSiteAnnouncementManager, SiteAnnouncementManager } from 'core/siteAnnouncement/SiteAnnouncementManager';
import { FormConfig, FormSection } from 'components/form/FormConfig';
import { getByteLength } from 'utils/StringUtil';
import { FormikContextType, FormikErrors } from 'formik';
import styles from './siteAnnouncementForm.module.scss';
import { createSelectOptionsFromEnum } from 'utils/SelectOptionsUtils';
import { useLocation, useParams } from 'react-router-dom';
import { renderAddUrlModal } from './AddUrlModal';

// 限時通知的預設天數（既有預設值，示意圖上也是 30）
const DEFAULT_DURATION_IN_DAYS = 30;

/**
 * 新增頁的通知分類來自入口 —— 列表頁的「新增」按鈕會帶 `?type=`。
 *
 * 回 `undefined` 表示「不是從分類入口進來的」（沒帶、或帶了不認識的值）：
 * 那種情況分類退回系統公告**而且維持可改**，所以直接打 `/site-announcements/new`
 * 的舊書籤行為與改動前一致。反過來，從入口進來就鎖定該分類 —— 需求是「從哪個
 * 入口進去就固定建立那個類型」，而分類建立後不可編輯，鎖住可以少一次無法挽回的誤點。
 */
export const resolveTypeFromSearch = (search: string): SiteAnnouncementType | undefined => {
  const type = new URLSearchParams(search).get('type');
  return Object.values(SiteAnnouncementType).includes(type as SiteAnnouncementType)
    ? (type as SiteAnnouncementType)
    : undefined;
};

export type SiteAnnouncementSetupFlowPageModel = {
  loading: boolean;
  title: string;
  redirectPath?: string;
  siteAnnouncement?: SiteAnnouncementFormData;
  allVendorNumberOptions: SelectOptions[];
  validateForm: (values: SiteAnnouncementFormData) => Record<string, string | undefined>;
  getFormConfig: (props: FormikContextType<SiteAnnouncementFormData>) => FormConfig;
  onSubmit: (values: SiteAnnouncementFormData) => void;
  onCancel: () => void;
  setSiteAnnouncement: (siteAnnouncement: SiteAnnouncementFormData) => void;
};

type BaseSiteAnnouncementSetupFlowPageModel = {
  loading: boolean;
  redirectPath?: string;
  allVendorNumberOptions: SelectOptions[];
  callAPIs: (
    apiCalls: (() => Promise<any>)[],
    onSuccess?: (...args: any[]) => void
  ) => void;
  onCancel: () => void;
  setRedirectPath: (path: string) => void;
  getBasicSection: (
    name: string,
    duration: number,
    durationType: SiteAnnouncementDurationType,
    typeDisabled: boolean,
    durationTypeDisabled: boolean
  ) => FormSection;
  getVenderNumbersSection: (
    chooseVendorStrategy: ChooseVendorStrategy,
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean
    ) => Promise<void | FormikErrors<SiteAnnouncementFormData>>
  ) => FormSection;
  validateForm: (
    values: SiteAnnouncementFormData,
    originalPublishTime?: string
  ) => Record<string, string | undefined>;
};

const chooseVendorStrategyOptions = createSelectOptionsFromEnum(
  ChooseVendorStrategy,
  'siteAnnouncementForm.labels.',
  [],
  (value) => `${value.toLowerCase()}Vendors`
);

const siteAnnouncementTypeOptions = createSelectOptionsFromEnum(
  SiteAnnouncementType,
  'siteAnnouncementForm.labels.types.'
);

const durationTypeOptions = createSelectOptionsFromEnum(
  SiteAnnouncementDurationType,
  'siteAnnouncementForm.labels.durationTypes.'
);

const defaultAgencyManager: AgencyManager = new DefaultAgencyManager();
const defaultSiteAnnouncementManager: SiteAnnouncementManager = new DefaultSiteAnnouncementManager();

const useBaseSiteAnnouncementSetupFlowPageModel = (): BaseSiteAnnouncementSetupFlowPageModel => {

  const { loading, callAPIs } = useCallAPI();
  const [redirectPath, setRedirectPath] = useState<string>();
  const [allVendorNumberOptions, setAllVendorNumberOptions] = useState<SelectOptions[]>([]);
  const contentRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    callAPIs([() => defaultAgencyManager.getAgencyVendorNumberOptions(true)], (vendorNumberOptions: SelectOptions[]) => {
      setAllVendorNumberOptions(vendorNumberOptions);
    });
  }, [callAPIs]);

  /**
   * @param typeDisabled         通知分類。編輯模式一律鎖（建立後不可編輯，BE 也用 ignore list 擋）；
   *                             新增模式只在「從分類入口進來」時鎖 —— 需求是入口決定分類
   * @param durationTypeDisabled 通知時長（常駐 / 限時）。同樣建立後不可編輯，但新增模式一律可選；
   *                             ⚠️ 與分類分成兩個旗標，否則新增頁鎖了分類會連通知時長一起鎖掉。
   *                             限時的天數兩種模式都可編輯
   */
  const getBasicSection = useCallback(
    (
      name: string,
      duration: number,
      durationType: SiteAnnouncementDurationType,
      typeDisabled: boolean,
      durationTypeDisabled: boolean
    ) =>
      new FormConfig.SectionBuilder(
        new FormConfig.FieldsBuilder()
          .addFormikDatePicker({
            label: i18n.t<string>('siteAnnouncementForm.labels.publishTime'),
            name: 'publishTime',
            showTimePicker: true,
            inputFormat: 'YYYY-MM-DD HH:mm:ss',
            minDate: moment()
              .add(1, 'hour')
              .startOf('hour')
              .format('YYYY-MM-DD HH:mm:ss')
          })
          .addFormikInput({
            label: i18n.t<string>('siteAnnouncementForm.labels.name'),
            name: 'name',
            className: styles.nameInput,
            postText: `${getByteLength(name)}/50`,
            postTextClassName: styles.countLimit
          })
          .addFormikSelect({
            label: i18n.t<string>('siteAnnouncementForm.labels.type'),
            name: 'type',
            options: siteAnnouncementTypeOptions,
            simpleValue: true,
            disabled: typeDisabled
          })
          .addFormikTextarea({
            label: i18n.t<string>('siteAnnouncementForm.labels.content'),
            name: 'content',
            innerRef: contentRef
          })
          .addCustom({
            label: '',
            name: 'addUrl',
            render: () => renderAddUrlModal(contentRef)
          })
          .addFormikRadio({
            label: i18n.t<string>('siteAnnouncementForm.labels.durationType'),
            name: 'durationType',
            options: durationTypeOptions,
            disabled: durationTypeDisabled
          })
          .addFormikInput({
            // 標籤留空：上一列的 radio 已經是「通知時長」，這一列只是它選了限時之後的天數，
            // 兩列都掛同一個標籤是重複的（示意圖上這一列左側也沒有標籤）
            label: '',
            name: 'duration',
            type: 'number',
            fieldContentWidth: 64,
            fieldContentClassName: styles.durationFieldContent,
            min: 1,
            preComponent: i18n.t<string>(
              'siteAnnouncementForm.labels.prefix.duration'
            ),
            postComponent: i18n.t<string>(
              'siteAnnouncementForm.labels.postfix.duration',
              {
                plural: duration > 1 ? 's' : ''
              }
            )
          }, durationType !== SiteAnnouncementDurationType.LIMITED)
          .build()
      )
        .withTitle(
          i18n.t<string>('siteAnnouncementForm.titles.basicSettingSection')
        )
        .build(),
    []
  );

  const getVenderNumbersSection = useCallback(
    (
      chooseVendorStrategy: ChooseVendorStrategy,
      setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean
      ) => Promise<void | FormikErrors<SiteAnnouncementFormData>>
    ) => {
      const fieldsBuilder = new FormConfig.FieldsBuilder();
      fieldsBuilder.addFormikRadio({
        label: i18n.t<string>(
          'siteAnnouncementForm.labels.chooseVendorStrategy'
        ),
        name: 'chooseVendorStrategy',
        options: chooseVendorStrategyOptions,
        onChange: () => {
          setFieldValue('vendorNumbers', []);
        }
      });
      if (chooseVendorStrategy === ChooseVendorStrategy.PARTIAL) {
        fieldsBuilder.addFormikTags({
          label: '',
          name: 'vendorNumbers',
          placeholder: i18n.t<string>(
            'siteAnnouncementForm.placeholders.vendorNumbers'
          ),
          suggestions: allVendorNumberOptions,
          onChange: (tagValues: string[]) => {
            setFieldValue(
              'vendorNumbers',
              flatMap(tagValues, (tagValue) => tagValue.split(','))
            );
          }
        });
      }
      return new FormConfig.SectionBuilder(fieldsBuilder.build())
        .withTitle(
          i18n.t<string>('siteAnnouncementForm.titles.vendorNumbersSection')
        )
        .build();
    },
    [allVendorNumberOptions]
  );

  const validatePublishTime = useCallback((publishTime: string) => {
    const error = validateEmpty(publishTime);
    if (error) {
      return error;
    }
    const publishMoment = moment(publishTime, 'YYYY-MM-DD HH:mm:ss');
    if (publishMoment.isBefore(moment().add(1, 'hour').startOf('hour'))) {
      return i18n.t<string>('siteAnnouncementForm.errors.publishTimeTooEarly');
    }
    return undefined;
  }, []);

  const validateVendorNumbers = useCallback(
    (chooseVendorStrategy: ChooseVendorStrategy, vendorNumbers: string[]) => {
      if (chooseVendorStrategy === ChooseVendorStrategy.ALL) {
        return;
      }

      const error = validateEmpty(vendorNumbers);
      if (error) {
        return error;
      }
      const allVendorNumbers = allVendorNumberOptions.map(
        (option) => option.value
      );
      return vendorNumbers.some(
        (vendorNumber: string) => !allVendorNumbers.includes(vendorNumber)
      )
        ? i18n.t<string>('siteAnnouncementForm.errors.vendorNumbersInvalid')
        : undefined;
    },
    [allVendorNumberOptions]
  );

  const validInputFieldValidityAndLength = useCallback(
    (value: string, maxLength: number, lengthErrorMessage: string, validityErrorMessage: string) => {
      const lengthError = validateStringMaxLength(value, maxLength, lengthErrorMessage);
      if (lengthError) {
        return lengthError;
      }
      const validityError = validateValidWords(value, validityErrorMessage);
      if (validityError) {
        return validityError;
      }
      return undefined;
    },
    []
  );

  const validateContent = useCallback(
    (content: string) => {
      const replacedContent = content.replace(
        new RegExp(`{${URL_REGEX.source}}`, 'g'),
        '$1'
      );
      return validInputFieldValidityAndLength(
        replacedContent,
        300,
        i18n.t<string>('siteAnnouncementForm.errors.contentLength'),
        i18n.t<string>('siteAnnouncementForm.errors.contentInvalid')
      );
    },
    [validInputFieldValidityAndLength]
  );

  /**
   * @param originalPublishTime 載入時的發佈時間。給了、而且使用者沒動它，就跳過「最早只能設定下一個
   *                            小時」那道檢查 —— 一則已發佈的通知發佈時間必然在過去，照驗會讓它連
   *                            內容都改不了（BE 同步放寬，見 validateUpdate）。改動發佈時間仍要驗。
   */
  const validateForm = useCallback(
    (values: SiteAnnouncementFormData, originalPublishTime?: string) => {
      const publishTimeUnchanged =
        originalPublishTime !== undefined && values.publishTime === originalPublishTime;
      return omitBy(
        {
          name: validInputFieldValidityAndLength(
            values.name,
            50,
            i18n.t<string>('siteAnnouncementForm.errors.nameLength'),
            i18n.t<string>('siteAnnouncementForm.errors.nameInvalid')
          ),
          content: validateContent(values.content),
          publishTime: publishTimeUnchanged
            ? undefined
            : validatePublishTime(values.publishTime),
          type: validateEmpty(values.type),
          durationType: validateEmpty(values.durationType),
          // 常駐通知沒有天數可驗
          duration:
            values.durationType === SiteAnnouncementDurationType.LIMITED
              ? validateMinimum(values.duration, 1)
              : undefined,
          vendorNumbers: validateVendorNumbers(
            values.chooseVendorStrategy,
            values.vendorNumbers
          )
        },
        isEmpty
      );
    },
    [
      validatePublishTime,
      validateVendorNumbers,
      validInputFieldValidityAndLength,
      validateContent
    ]
  );

  const onCancel = useCallback(() => {
    setRedirectPath('/site-announcements');
  }, []);

  return {
    loading,
    redirectPath,
    allVendorNumberOptions,
    callAPIs,
    onCancel,
    validateForm,
    setRedirectPath,
    getBasicSection,
    getVenderNumbersSection
  };
};

export const useCreateSiteAnnouncementSetupFlowPageModel = (): SiteAnnouncementSetupFlowPageModel => {
  const {
    loading,
    redirectPath,
    allVendorNumberOptions,
    callAPIs,
    onCancel,
    validateForm,
    setRedirectPath,
    getBasicSection,
    getVenderNumbersSection
  } = useBaseSiteAnnouncementSetupFlowPageModel();

  const { search } = useLocation();
  // undefined = 不是從分類入口進來的（舊書籤）⇒ 預設系統公告但不鎖
  const entryType = resolveTypeFromSearch(search);
  const [siteAnnouncement, setSiteAnnouncement] = useState<SiteAnnouncementFormData>({
    // 最早只能設定當前下一個小時發佈
    publishTime: moment().add(1, 'hour').startOf('hour').format('YYYY-MM-DD HH:mm:ss'),
    name: '',
    content: '',
    type: entryType ?? SiteAnnouncementType.SYSTEM,
    durationType: SiteAnnouncementDurationType.LIMITED,
    duration: DEFAULT_DURATION_IN_DAYS,
    vendorNumbers: [],
    shouldResend: false,
    chooseVendorStrategy: ChooseVendorStrategy.ALL
  });

  const onSubmit = useCallback((_values: SiteAnnouncementFormData) => {
    callAPIs(
      [() => defaultSiteAnnouncementManager.createSiteAnnouncement(_values)],
      () => {
        // 導到「該分類」的列表頁：'/site-announcements' 會被 redirect 到系統公告，
        // 建立一則活動優惠後會落在看不到它的那一頁
        setRedirectPath(SITE_ANNOUNCEMENT_LIST_PATH[_values.type ?? SiteAnnouncementType.SYSTEM]);
      }
    );
  }, [callAPIs, setRedirectPath]);

  const getFormConfig = useCallback(
    ({
      values,
      setFieldValue
    }: FormikContextType<SiteAnnouncementFormData>) => {
      const { name, duration, durationType, chooseVendorStrategy } = values;

      return new FormConfig.Builder()
        .addSection(
          getBasicSection(name, duration, durationType, entryType !== undefined, false)
        )
        .addSection(
          getVenderNumbersSection(chooseVendorStrategy, setFieldValue)
        )
        .build();
    },
    [entryType, getBasicSection, getVenderNumbersSection]
  );

  return {
    title: i18n.t<string>('siteAnnouncementForm.titles.create'),
    loading,
    redirectPath,
    siteAnnouncement,
    allVendorNumberOptions,
    onCancel,
    onSubmit,
    validateForm,
    getFormConfig,
    setSiteAnnouncement
  };
};

export const useEditSiteAnnouncementSetupFlowPageModel = (): SiteAnnouncementSetupFlowPageModel => {

  const {
    loading,
    redirectPath,
    allVendorNumberOptions,
    callAPIs,
    onCancel,
    validateForm,
    setRedirectPath,
    getBasicSection,
    getVenderNumbersSection
  } = useBaseSiteAnnouncementSetupFlowPageModel();

  const [siteAnnouncement, setSiteAnnouncement] = useState<SiteAnnouncementFormData>();
  const [isPublished, setIsPublished] = useState<boolean>(false);
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    callAPIs([
      () => defaultSiteAnnouncementManager.getSiteAnnouncement(+id)
    ], (siteAnnouncement: SiteAnnouncement) => {
      setSiteAnnouncement({
        ...siteAnnouncement,
        // 相容期的舊資料沒有這兩欄（BE backfill 前），沒 fallback 會讓 radio 拿到 undefined 而爆掉
        // PERMANENT 的通知 BE 回 duration = null，表單那格用不到但型別要求 number
        duration: siteAnnouncement.duration ?? DEFAULT_DURATION_IN_DAYS,
        type: siteAnnouncement.type ?? SiteAnnouncementType.SYSTEM,
        durationType: siteAnnouncement.durationType ?? SiteAnnouncementDurationType.LIMITED,
        vendorNumbers: siteAnnouncement.vendorNumbers || [],
        chooseVendorStrategy: isEmpty(siteAnnouncement.vendorNumbers)
          ? ChooseVendorStrategy.ALL
          : ChooseVendorStrategy.PARTIAL
      });
      setIsPublished(siteAnnouncement.status === SiteAnnouncementStatus.PUBLISHED);
    });
  }, [id, callAPIs]);

  const onSubmit = useCallback((_values: SiteAnnouncementFormData) => {
    callAPIs(
      [() => defaultSiteAnnouncementManager.updateSiteAnnouncement(+id, _values)],
      () => {
        // 同上：分類不可編輯，所以 _values.type 就是這則通知所屬的分類
        setRedirectPath(SITE_ANNOUNCEMENT_LIST_PATH[_values.type ?? SiteAnnouncementType.SYSTEM]);
      }
    );
  }, [id, callAPIs, setRedirectPath]);

  const getFormConfig = useCallback(
    ({ values, setFieldValue }: FormikContextType<SiteAnnouncementFormData>) => {
      const { name, duration, durationType, chooseVendorStrategy } = values;
      // 編輯模式：通知分類與通知時長顯示現值但不可編輯
      const basicSection = getBasicSection(name, duration, durationType, true, true);

      if (isPublished) {
        basicSection.basicFields = new FormConfig.FieldsBuilder(
          basicSection.basicFields
        )
          .addFormikCheckbox({
            label: i18n.t<string>('siteAnnouncementForm.labels.shouldResend'),
            name: 'shouldResend'
          })
          .build();
      }

      return new FormConfig.Builder()
        .addSection(basicSection)
        .addSection(getVenderNumbersSection(chooseVendorStrategy, setFieldValue))
        .build();
    },
    [isPublished, getBasicSection, getVenderNumbersSection]
  );

  // 發佈時間沒被動過就別驗它是不是未來（見 validateForm 的 originalPublishTime）
  const validateEditForm = useCallback(
    (values: SiteAnnouncementFormData) => validateForm(values, siteAnnouncement?.publishTime),
    [validateForm, siteAnnouncement]
  );

  return {
    title: i18n.t<string>('siteAnnouncementForm.titles.edit'),
    loading,
    redirectPath,
    siteAnnouncement,
    allVendorNumberOptions,
    onSubmit,
    onCancel,
    validateForm: validateEditForm,
    getFormConfig,
    setSiteAnnouncement
  };
};
